import { Box, Button, Heading, HStack, IconButton, Text, VStack } from '@chakra-ui/react';
import { Xmark } from 'iconoir-react';
import type { ReactNode } from 'react';

type AnnouncementAccent = 'lilac' | 'green' | 'neutral';
type AnnouncementTone = 'dark' | 'light';

const ACCENT_GLOW: Record<AnnouncementAccent, string> = {
  lilac: 'rgba(130, 104, 192, 0.40)',
  green: 'rgba(36, 164, 129, 0.32)',
  neutral: 'rgba(255, 255, 255, 0.10)',
};

// Scrim layered over the background image so the copy stays legible. Dark tone
// darkens for white text; light tone lays a cream wash for charcoal text. Both
// fade out toward the right so the artwork (and the end slot) stays visible.
const SCRIM: Record<AnnouncementTone, string> = {
  dark: 'linear-gradient(90deg, rgba(12,13,16,0.92), rgba(12,13,16,0.58) 58%, rgba(12,13,16,0.30))',
  light:
    'linear-gradient(90deg, rgba(251,250,246,0.92), rgba(251,250,246,0.55) 56%, rgba(251,250,246,0.12))',
};

export interface AnnouncementBannerProps {
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  onClose?: () => void;
  /** Optional background image; a tone-matched scrim is layered on top. */
  backgroundImage?: string;
  /** Accent glow tint (dark tone only). */
  accent?: AnnouncementAccent;
  /**
   * Surface tone. 'dark' (default) = charcoal surface, white text. 'light' =
   * pastel surface, charcoal text. Pick the tone that matches backgroundImage so
   * the copy stays legible.
   */
  tone?: AnnouncementTone;
  /** Optional right-side visual (portrait cluster, illustration). */
  endSlot?: ReactNode;
}

/**
 * Dismissible feature-announcement banner. Editorial surface with an optional
 * background image, used to surface configurable features (AI companion...).
 */
export const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({
  title,
  description,
  actionLabel,
  onAction,
  onClose,
  backgroundImage,
  accent = 'lilac',
  tone = 'dark',
  endSlot,
}) => {
  const isLight = tone === 'light';

  return (
    <Box
      position="relative"
      overflow="hidden"
      rounded="xl"
      px={{ base: 5, md: 8 }}
      py={{ base: 5, md: 7 }}
      minH="148px"
      color={isLight ? 'fg' : 'white'}
      bg={isLight ? 'softCream' : '#15171b'}
      backgroundImage={backgroundImage ? `${SCRIM[tone]}, url(${backgroundImage})` : undefined}
      backgroundSize="cover"
      backgroundPosition="center"
      _before={
        isLight
          ? undefined
          : {
              content: '""',
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background: `radial-gradient(120% 100% at 8% -10%, ${ACCENT_GLOW[accent]}, transparent 46%)`,
            }
      }
    >
      {onClose && (
        <IconButton
          aria-label="Fermer"
          onClick={onClose}
          position="absolute"
          top={3}
          right={3}
          boxSize={7}
          minW={7}
          rounded="full"
          variant="plain"
          color={isLight ? 'fg.muted' : 'whiteAlpha.800'}
          bg={isLight ? 'blackAlpha.50' : 'whiteAlpha.100'}
          borderWidth="1px"
          borderColor={isLight ? 'blackAlpha.200' : 'whiteAlpha.300'}
          _hover={
            isLight
              ? { bg: 'blackAlpha.100', color: 'fg' }
              : { bg: 'whiteAlpha.300', color: 'white' }
          }
        >
          <Xmark width={15} height={15} />
        </IconButton>
      )}

      <HStack justify="space-between" align="center" gap={6} position="relative">
        <VStack align="flex-start" gap={4} maxW="58ch">
          <VStack align="flex-start" gap={1.5}>
            <Heading
              size="lg"
              fontFamily="heading"
              color={isLight ? 'fg' : 'white'}
              letterSpacing="-0.01em"
            >
              {title}
            </Heading>
            {description && (
              <Text fontSize="sm" color={isLight ? 'fg.muted' : 'whiteAlpha.800'} lineHeight="1.5">
                {description}
              </Text>
            )}
          </VStack>
          {actionLabel && (
            <Button
              size="sm"
              onClick={onAction}
              {...(isLight
                ? { colorPalette: 'primary' as const }
                : { bg: 'softCream', color: 'dark', _hover: { bg: 'white' } })}
            >
              {actionLabel}
            </Button>
          )}
        </VStack>

        {endSlot && (
          <Box flexShrink={0} display={{ base: 'none', md: 'block' }}>
            {endSlot}
          </Box>
        )}
      </HStack>
    </Box>
  );
};
